import {useI18n} from '@/i18n/I18n'
import Button from '@codegouvfr/react-dsfr/Button'
import {ForwardedRef, MouseEventHandler, ReactNode, forwardRef, useId} from 'react'
import {OptionalLabel} from './OptionalLabel'

type Props = {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void
  name: string
  placeholder?: string
  error: boolean
  helperText?: ReactNode
  required: boolean
  label?: ReactNode
  /** Rendered after the input (same row when space allows). Keeps interactive content out of `<label>` and puts it after the field in tab order. */
  labelAccessory?: ReactNode
  desc?: ReactNode
  autocomplete?: string
  type?: 'text' | 'email' | 'tel' | 'number'
  inputMode?: 'numeric'
  disabled?: boolean
  clearable?: {
    onClear: () => void
    label: string
  }
  editable?: {
    onEdit: () => void
    label: string
  }
  tabIndex?: number
  onClick?: MouseEventHandler<HTMLDivElement>
  disableLeftBorderOnError?: boolean
}
export const ScTextInput = forwardRef((props: Props, ref: ForwardedRef<HTMLInputElement>) => {
  const {
    onChange,
    onBlur,
    autocomplete,
    name,
    placeholder,
    label,
    labelAccessory,
    desc,
    error,
    helperText,
    required,
    type = 'text',
    inputMode,
    disabled = false,
    editable,
    clearable,
    onClick = () => {},
    disableLeftBorderOnError = false,
  } = props
  const {m} = useI18n()
  const inputId = useId()
  const helperTextId = useId()
  const labelWithAsterisk = (
    <>
      {label}
      <OptionalLabel {...{required}} />
    </>
  )
  return (
    <div
      className={`fr-input-group ${error ? 'fr-input-group--error' : ''} ${disabled ? 'fr-input-group--disabled' : ''} ${
        disableLeftBorderOnError ? 'before:!hidden' : ''
      } sctextinput`}
      onClick={onClick}
    >
      {label && (
        <label className={`fr-label ${disabled ? '!text-gray-600' : ''}`} htmlFor={inputId}>
          {labelWithAsterisk}
          {desc && <span className={`fr-hint-text ${disabled ? '!text-gray-600' : ''}`}>{desc}</span>}
        </label>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          autoComplete={autocomplete}
          id={inputId}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          ref={ref}
          type={type}
          {...(inputMode ? {inputMode} : null)}
          readOnly={disabled}
          placeholder={placeholder}
          className={`fr-input min-w-0 flex-1 ${error ? 'fr-input--error' : ''} ${disabled ? '!text-gray-600 !shadow-none' : ''}`}
          {...(helperText ? {'aria-describedby': helperTextId} : null)}
          {...(error ? {'aria-invalid': true} : null)}
          {...(required ? {'aria-required': true} : null)}
          // If the edit button is displayed
          // no need for the input itself to be accessible through keyboard
          {...(editable && disabled ? {tabIndex: -1} : null)}
        />
        {labelAccessory && <div className="shrink-0">{labelAccessory}</div>}
        {editable && (
          <Button
            iconId="fr-icon-edit-line"
            onClick={() => {
              document.getElementById(inputId)?.focus()
              editable.onEdit()
            }}
            priority="tertiary"
            title={editable.label}
          />
        )}
        {clearable && (
          <Button iconId="fr-icon-close-line" onClick={clearable.onClear} priority="tertiary" title={clearable.label} />
        )}
      </div>
      {helperText && (
        <p id={helperTextId} className={error ? 'fr-error-text' : 'fr-info-text'} {...(error ? {'aria-live': 'polite'} : null)}>
          {helperText}
        </p>
      )}
    </div>
  )
})
